import React from 'react'
import { useEffect, useState } from 'react'
import CommonForm, { ItemElementPropsInterface } from '../../../components/common/common-form/common-form'
import { commonFormSelect, getSelectOptions } from '../../../components/common/common-form/common-form-select'
import CommonFormPackagingTypeCascader from '../../../components/common/common-form/common-form-packaging-type-cascader'
import { Button, Col, Input, InputNumber, Row, Select, Upload } from 'antd'
import { urlKey } from '../../../services/api/api-urls'
import { InboxOutlined } from '@ant-design/icons'
import { baseUrl } from '../../../services/api/base-url'
import { ApiRequest } from '../../../services/api/api'
import { getCookie } from 'react-use-cookie'
import SweetAlertService from '../../../services/lib/utils/sweet-alert-service'
import { getAuthHeaders } from '../../../services/lib/utils/auth.utils'

interface IFormValues {
	productName: string,
	productCode: string,
	productTypeId: any,
	classificationId?: any,
	packagingTypeId: any,
	palletStackingId: any,
	alermLimit: number,
	description: string,
	noteOnQuote:string,
	images: string,
	minOrderQuantity: number,
	grossWeigh: string,
	cartonLength?: number,
	cartonWidth?: number,
	cartonHeight?: number,
}

const { Option } = Select

// Three-level product classification cascade (Primary -> Child -> Grandchild)
// Antd Form injects value/onChange so it can be used directly as CommonForm inputElement
const ClassificationCascade = (props: { value?: any, onChange?: (v: any) => void }) => {
  const [options, setOptions] = useState<any[]>([])
  const [primaryId, setPrimaryId] = useState<number | undefined>(undefined)
  const [childId, setChildId] = useState<number | undefined>(undefined)
  const [grandchildId, setGrandchildId] = useState<number | undefined>(undefined)

  useEffect(() => {
    ApiRequest({ url: 'ProductClassification/GetAll?includeDeleted=false', method: 'get', isShowSpinner: false })
      .then(res => setOptions(res.data?.data || []))
      .catch(() => setOptions([]))
  }, [])

  // Derive cascade path from the incoming classificationId (deepest selected node)
  useEffect(() => {
    if (!options.length) return
    const map: any = {}
    options.forEach((c: any) => { map[c.classificationId] = c })
    const path: any[] = []
    let cur = props.value ? map[props.value] : null
    while (cur) {
      path.unshift(cur)
      cur = cur.parentId ? map[cur.parentId] : null
    }
    setPrimaryId(path[0]?.classificationId)
    setChildId(path[1]?.classificationId)
    setGrandchildId(path[2]?.classificationId)
  }, [props.value, options])

  const childrenOf = (parentId: number | null | undefined) =>
    options.filter((c: any) => parentId == null ? !c.parentId : c.parentId === parentId)

  return (
    <Row gutter={4}>
      <Col span={8}>
        <Select
          size="small" style={{ width: '100%' }} placeholder="Primary" allowClear showSearch optionFilterProp="children"
          value={primaryId}
          onChange={(v: any) => { setPrimaryId(v); setChildId(undefined); setGrandchildId(undefined); props.onChange && props.onChange(v ?? null) }}
        >
          {childrenOf(null).map((c: any) => <Option key={c.classificationId} value={c.classificationId}>{c.name}</Option>)}
        </Select>
      </Col>
      <Col span={8}>
        <Select
          size="small" style={{ width: '100%' }} placeholder="Child" allowClear showSearch optionFilterProp="children"
          value={childId} disabled={!primaryId}
          onChange={(v: any) => { setChildId(v); setGrandchildId(undefined); props.onChange && props.onChange(v ?? primaryId ?? null) }}
        >
          {childrenOf(primaryId).map((c: any) => <Option key={c.classificationId} value={c.classificationId}>{c.name}</Option>)}
        </Select>
      </Col>
      <Col span={8}>
        <Select
          size="small" style={{ width: '100%' }} placeholder="Grandchild" allowClear showSearch optionFilterProp="children"
          value={grandchildId} disabled={!childId}
          onChange={(v: any) => { setGrandchildId(v); props.onChange && props.onChange(v ?? childId ?? null) }}
        >
          {childrenOf(childId).map((c: any) => <Option key={c.classificationId} value={c.classificationId}>{c.name}</Option>)}
        </Select>
      </Col>
    </Row>
  )
}

const AddOutsourceProductDialog = (props: { onDialogClose: any, product?: any }) => {
  const [formRef, setFormRef] = useState<any>()
  const [initFormValues, setInitFormValues] = useState<IFormValues>()

  // store selection options from apis request
  const [productTypeOptions, setProductTypeOptions] = useState([])
  const [palletStackingOptions, setPalletStackingOptionss] = useState([])
  const [img, setImg] = useState<any>()

  useEffect(() => {
	  getSelectOptions(urlKey.ProductType).then(res => {
	    setProductTypeOptions(res)
	    // Product Type: keep the original value when editing; default to "Others" (productTypeId = 16) when new/missing
	    if (!props.product?.baseProduct?.productTypeId) {
	      const othersType: any = res.find((row: any) => row.productTypeId === 16)
	      if (othersType) {
	        setInitFormValues((prev: any) => ({ ...(prev || {}), productTypeId: othersType.productTypeId }))
	    }
	    }
	  })
	  getSelectOptions(urlKey.PalletStacking).then(res => setPalletStackingOptionss(res))
		console.log(props.product)
	  props.product && setInitFormValues({
		productName: props.product.productName,
		productCode: props.product.productCode,
		productTypeId: props.product.baseProduct.productTypeId,
		classificationId: props.product.baseProduct.classificationId ?? undefined,
		packagingTypeId: props.product.baseProduct.packagingTypeId,
		palletStackingId: props.product.palletStackingId,
		alermLimit: props.product.productMsl,
		description: props.product.description,
		noteOnQuote: props.product.baseProduct.rawmaterialDesc,
		images: props.product.images,
		minOrderQuantity: props.product.minOrderQuantity,
		grossWeigh: props.product.baseProduct.grossWeigh,
		cartonLength: props.product.baseProduct.cartonLength,
		cartonWidth: props.product.baseProduct.cartonWidth,
		cartonHeight: props.product.baseProduct.cartonHeight
	  })
  }, [])

  const formItems: ItemElementPropsInterface[] | any = [
	  {name: 'productName', label: 'Product Description',span: 24, rules: [{required: true}], inputElement: <Input />},
	  {name: 'productCode', label: 'Product Code',span:12, rules: [{required: true}], inputElement: <Input maxLength={36} showCount />},
	  {name: 'productTypeId', label: 'Product Type',span: 12, rules: [{required: true}], inputElement: commonFormSelect(urlKey.ProductType, productTypeOptions)},
	  {name: 'classificationId', label: 'Product Classification', span: 24, inputElement: <ClassificationCascade />},

	  // Packaging Type 单独一行
	  {name: 'packagingTypeId', label: 'Packaging Type', span: 24, rules: [{required: true}], inputElement: <CommonFormPackagingTypeCascader />},

	  // 其他信息分两行
	  {name: 'palletStackingId', label: 'Pallet Stacking', span: 8,rules: [{required: true}], inputElement: commonFormSelect(urlKey.PalletStacking, palletStackingOptions)},
	  {name: 'minOrderQuantity', label: 'Min Order Qty (Cartons)',span: 8, rules: [{required: true}], inputElement: <InputNumber />},
	  {name: 'alermLimit', label: 'Min Stock Level (Units)',span: 8, rules: [{required: true}], inputElement: <InputNumber />},

	  {name: 'description', label: 'Base/Outsourced Product Note', span: 12,inputElement: <Input.TextArea showCount={true} maxLength={150} autoSize={ true } />},
	  {name: 'noteOnQuote', label: 'Product Specification (Will show on Quote)', span: 12,inputElement: <Input.TextArea showCount={true} maxLength={150} autoSize={ true } />},

	  {name: 'grossWeigh', label: 'Gross Weight(kg)', span: 8,  inputElement: <Input />},
	  {name: 'cartonLength', label: 'Carton Length(mm)', span: 6,  inputElement: <InputNumber />},
	  {name: 'cartonWidth', label: 'Carton Width(mm)', span: 5,  inputElement: <InputNumber />},
	  {name: 'cartonHeight', label: 'Carton Height(mm)', span: 5,  inputElement: <InputNumber />},
  ]

  const onFormChange = (changedValues: any, newValues: any, form: any) => null

  const onFormBlur = (form: any) => {
    if (!formRef) {
      setFormRef(form)
    }
  }

  const onConfirm = async () => {
    formRef.submit()
    const formValues: IFormValues = await formRef.validateFields()
    if (formValues) {
	    // 检查 Product Code 是否超过 23 个字符
	    if (formValues.productCode && formValues.productCode.length > 36) {
	      const result = await SweetAlertService.warningConfirm(
	        `Product Code exceeds 36 characters (${formValues.productCode.length} chars). Do you want to continue saving?`,
	        'Warning'
	      );
	      if (result.dismissed) {
	        return; // 用户取消，中止保存
	      }
	    }

	    console.log(formValues)
	    props.product ?
		ApiRequest({url: 'Product/UpdateOutSourceProd', method: 'put', data: {
			productId: props.product.productId,
			productName: formValues.productName,
			productCode: formValues.productCode,
			productTypeId: formValues.productTypeId,
			classificationId: formValues.classificationId ?? null,
			packagingTypeId: formValues.packagingTypeId,
			palletStackingId:formValues.palletStackingId,
			description: formValues.description,
			minOrderQuantity:formValues.minOrderQuantity,
			noteOnQuote:formValues.noteOnQuote,
			AlermLimit:formValues.alermLimit,
			grossWeigh:formValues.grossWeigh,
			cartonLength:formValues.cartonLength ?? null,
			cartonWidth:formValues.cartonWidth ?? null,
			cartonHeight:formValues.cartonHeight ?? null,
			employeeId: parseInt(getCookie('id')),
		}}).then((res: any) => {
			props.onDialogClose(true, false)
		}) :
		ApiRequest({
		    url: 'Product/AddOutSourceProd',
		    method: 'post',
		    data: {
		    	...formValues,
		    	classificationId: formValues.classificationId ?? null,
				employeeId: parseInt(getCookie('id')),
			    images: img || null,
			    cartonLength: formValues.cartonLength ?? null,
			    cartonWidth: formValues.cartonWidth ?? null,
			    cartonHeight: formValues.cartonHeight ?? null,
		    }
	    }).then((res: any) => {
		    console.log(res)
	    	props.onDialogClose(true, true,res.data.data)
	    })
    }
  }

  return (
    <div style={ {width: '97%', margin: '0 auto 1rem'} }>
      <CommonForm
	      items={ formItems }
	      onFormChange={onFormChange}
	      onFormBlur={onFormBlur}
		  initFormValues={initFormValues}
      />
	    {!props.product ? <Upload.Dragger
		    name="imageFile"
		    multiple={false}
		    action={baseUrl + 'Common/UploadImage'}
		    headers={getAuthHeaders()}
		    onChange={(info: any) => {
			    if (info?.file?.response) {
			    	setImg('{"uid":1,"name":"' + info?.file?.name + '","url":"' + info?.file?.response + '"}')
			    }
		    }}
	    >
		    <p className="ant-upload-drag-icon">
			    <InboxOutlined />
		    </p>
		    <p className="ant-upload-text">Click or drag file to this area to upload</p>
	    </Upload.Dragger> : null}
      <div style={{display: 'flex', justifyContent: 'flex-end'}}>
        <Button
          onClick={() => {
            props.onDialogClose(false)
          }}
          style={{marginRight: '2rem'}}
        >Cancel</Button>
        <Button
          disabled={!formRef}
          onClick={onConfirm}
          type="primary"
        >Confirm</Button>
      </div>
    </div>
  )
}

export default AddOutsourceProductDialog
